import { TFunction } from 'i18next';
import { IntegrationConfigBase } from 'models/integrations/integrationConfigBase';
import { IIntegrationEvidence } from 'models/integrations/integrationEvidence';
import { toLocaleDateTime } from 'utils/datetime';
import { entraAdministratorRoles, getEntraRoleName, MS_CAAdminMFAProblem } from './IntegrationConfig_MS_CAAdminMFA';

//The applications a policy must block, as flags; mirrors MS_AdminPortalRestrictionHandler.AdminPortalTargets
export enum AdminPortalTargets {
  None = 0,
  AdminPortals = 1,
  AzureManagement = 2,
}

export const allAdminPortalTargets = AdminPortalTargets.AdminPortals | AdminPortalTargets.AzureManagement;

//
// Config class
//
export class IntegrationConfig_MS_AdminPortalRestriction extends IntegrationConfigBase {
  requiredTargets: AdminPortalTargets;

  //the administrative roles that keep access
  adminRoleIds: string[];

  //every listed role must be excluded; false: Global Administrator is enough
  requireAllRolesExcluded: boolean;

  createChecklistItemPerProblem: boolean;

  constructor() {
    super();
    this.requiredTargets = allAdminPortalTargets;
    this.adminRoleIds = entraAdministratorRoles.map((role) => role.id);
    this.requireAllRolesExcluded = false;
    this.createChecklistItemPerProblem = true;
  }
}

//
// Evidence classes
//
export class MS_AdminPortalRestrictionTarget {
  //AdminPortals or AzureManagement
  target: string = '';

  applicationId: string = '';

  covered: boolean = false;

  winningPolicyId?: string;

  winningPolicyName?: string;

  closestPolicyId?: string;

  closestPolicyName?: string;

  problems: MS_CAAdminMFAProblem[] = [];

  //role names
  excludedRoles: string[] = [];

  excludedUsers: string[] = [];

  excludedGroups: string[] = [];

  excludedUserCount: number = 0;

  excludedGroupCount: number = 0;
}

export class MS_AdminPortalRestrictionEvidence implements IIntegrationEvidence {
  isCapped: boolean = false;

  evaluatedAtUtc: Date = new Date();

  policyCount: number = 0;

  targets: MS_AdminPortalRestrictionTarget[] = [];

  pass: boolean = false;

  toMarkdownReport(t: TFunction<['translation', 'integrations']>): string {
    const sb: string[] = [];
    const typedT = t as TFunction<string[]>;

    sb.push(
      `## ${t('integrations:MS_AdminPortalRestriction.Report.SummaryHeading', {
        date: toLocaleDateTime(this.evaluatedAtUtc),
      })}`,
    );
    sb.push(
      `- ${
        this.pass
          ? t('integrations:MS_AdminPortalRestriction.Outcome.Pass')
          : t('integrations:MS_AdminPortalRestriction.Outcome.Fail')
      }`,
    );
    sb.push(`- ${t('integrations:MS_AdminPortalRestriction.Report.PolicyCount', { number: this.policyCount })}`);

    if (this.targets.length > 0) {
      sb.push('');
      sb.push(
        `| ${t('integrations:MS_AdminPortalRestriction.Columns.Target')} | ${t(
          'integrations:MS_AdminPortalRestriction.Columns.Policy',
        )} | ${t('integrations:MS_AdminPortalRestriction.Columns.Problems')} | ${t(
          'integrations:MS_AdminPortalRestriction.Columns.Result',
        )} |`,
      );
      sb.push('| --- | --- | --- | --- |');
      this.targets.forEach((target) => {
        const problems = target.problems.map((problem) => getAdminPortalProblemText(problem, typedT)).join('; ');
        const result = target.covered
          ? t('integrations:MS_AdminPortalRestriction.Result.Pass')
          : t('integrations:MS_AdminPortalRestriction.Result.Fail');
        sb.push(
          `| ${getAdminPortalTargetText(target.target, typedT)} | ${escapeCell(
            getTargetPolicyText(target, typedT),
          )} | ${escapeCell(problems)} | ${result} |`,
        );
      });
    }

    sb.push('');
    sb.push(t('integrations:MS_AdminPortalRestriction.ManualCheck'));

    return sb.join('\n');
  }
}

const escapeCell = (value: string): string => value.replace(/\|/g, '\\|');

//
// Texts shared by the panel and the report
//
export const getAdminPortalTargetText = (target: string, t: TFunction<string[]>): string => {
  switch (target) {
    case 'AdminPortals':
      return t('integrations:MS_AdminPortalRestriction.Targets.AdminPortals');
    case 'AzureManagement':
      return t('integrations:MS_AdminPortalRestriction.Targets.AzureManagement');
    default:
      return target;
  }
};

//the policy that blocks the target, else the closest block policy, else that there is none
export const getTargetPolicyText = (target: MS_AdminPortalRestrictionTarget, t: TFunction<string[]>): string => {
  if (target.covered) return target.winningPolicyName ?? '';
  if (target.closestPolicyName) return target.closestPolicyName;

  return t('integrations:MS_AdminPortalRestriction.Result.NoBlockPolicy');
};

//the values are ids: the role ids are named through the role table, other ids are shown as given
const getProblemValues = (problem: MS_CAAdminMFAProblem): string => {
  if (
    problem.code === 'RoleNotExcluded' ||
    problem.code === 'RolesNotIncluded' ||
    problem.code === 'RequiredRoleExcluded'
  ) {
    return problem.values.map((value) => getEntraRoleName(value)).join(', ');
  }

  return problem.values.join(', ');
};

export const getAdminPortalProblemText = (problem: MS_CAAdminMFAProblem, t: TFunction<string[]>): string => {
  const values = getProblemValues(problem);

  //translation keys cannot be built from variables, so every code has its own case
  switch (problem.code) {
    case 'Disabled':
      return t('integrations:MS_AdminPortalRestriction.Problems.Disabled');
    case 'NotAllUsers':
      return t('integrations:MS_AdminPortalRestriction.Problems.NotAllUsers');
    case 'RolesNotIncluded':
      return t('integrations:MS_AdminPortalRestriction.Problems.RolesNotIncluded', { values });
    case 'RequiredRoleExcluded':
      return t('integrations:MS_AdminPortalRestriction.Problems.RequiredRoleExcluded', { values });
    case 'NotAllApps':
      return t('integrations:MS_AdminPortalRestriction.Problems.NotAllApps');
    case 'DoesNotTarget':
      return t('integrations:MS_AdminPortalRestriction.Problems.DoesNotTarget', { values });
    case 'TargetExcluded':
      return t('integrations:MS_AdminPortalRestriction.Problems.TargetExcluded', { values });
    case 'AppsExcluded':
      return t('integrations:MS_AdminPortalRestriction.Problems.AppsExcluded', { values });
    case 'PlatformsLimited':
      return t('integrations:MS_AdminPortalRestriction.Problems.PlatformsLimited');
    case 'LocationsLimited':
      return t('integrations:MS_AdminPortalRestriction.Problems.LocationsLimited');
    case 'DeviceFilter':
      return t('integrations:MS_AdminPortalRestriction.Problems.DeviceFilter');
    case 'ClientAppTypesLimited':
      return t('integrations:MS_AdminPortalRestriction.Problems.ClientAppTypesLimited');
    case 'UserRiskCondition':
      return t('integrations:MS_AdminPortalRestriction.Problems.UserRiskCondition');
    case 'SignInRiskCondition':
      return t('integrations:MS_AdminPortalRestriction.Problems.SignInRiskCondition');
    case 'AuthenticationFlowsLimited':
      return t('integrations:MS_AdminPortalRestriction.Problems.AuthenticationFlowsLimited');
    case 'GrantMissing':
      return t('integrations:MS_AdminPortalRestriction.Problems.GrantMissing');
    case 'RoleNotExcluded':
      return t('integrations:MS_AdminPortalRestriction.Problems.RoleNotExcluded', { values });
    default:
      return problem.text || problem.code;
  }
};

const toStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item) => typeof item === 'string') : [];

export const parse_Evidence_MS_AdminPortalRestriction = (
  raw: string | undefined,
): MS_AdminPortalRestrictionEvidence | undefined => {
  if (!raw) return undefined;

  const rawEvidence = JSON.parse(raw);
  if (!rawEvidence) return undefined;

  const evidence = new MS_AdminPortalRestrictionEvidence();
  evidence.isCapped = rawEvidence.isCapped ?? false;
  evidence.evaluatedAtUtc = rawEvidence.evaluatedAtUtc ? new Date(rawEvidence.evaluatedAtUtc) : new Date();
  evidence.policyCount = rawEvidence.policyCount ?? 0;
  evidence.pass = rawEvidence.pass ?? false;
  evidence.targets = (Array.isArray(rawEvidence.targets) ? rawEvidence.targets : []).map(
    (rawTarget: MS_AdminPortalRestrictionTarget) => {
      const target = new MS_AdminPortalRestrictionTarget();
      target.target = rawTarget.target ?? '';
      target.applicationId = rawTarget.applicationId ?? '';
      target.covered = rawTarget.covered === true;
      target.winningPolicyId = rawTarget.winningPolicyId ?? undefined;
      target.winningPolicyName = rawTarget.winningPolicyName ?? undefined;
      target.closestPolicyId = rawTarget.closestPolicyId ?? undefined;
      target.closestPolicyName = rawTarget.closestPolicyName ?? undefined;
      target.problems = (Array.isArray(rawTarget.problems) ? rawTarget.problems : []).map(
        (rawProblem: MS_CAAdminMFAProblem) => {
          const problem = new MS_CAAdminMFAProblem();
          problem.code = rawProblem.code ?? '';
          problem.text = rawProblem.text ?? '';
          problem.values = toStringArray(rawProblem.values);

          return problem;
        },
      );
      target.excludedRoles = toStringArray(rawTarget.excludedRoles);
      target.excludedUsers = toStringArray(rawTarget.excludedUsers);
      target.excludedGroups = toStringArray(rawTarget.excludedGroups);
      target.excludedUserCount = rawTarget.excludedUserCount ?? target.excludedUsers.length;
      target.excludedGroupCount = rawTarget.excludedGroupCount ?? target.excludedGroups.length;

      return target;
    },
  );

  return evidence;
};
